import styled from "styled-components";
import { Trans } from "react-i18next";
import { Button, TextField } from "@mui/material";
import { useSetState } from "components/ReduxProvider";
import { UserEditQL } from "root/profile/UserEdit";
import { UserEditMutation } from "root/profile/__generated__/UserEditMutation.graphql";
import { useMutation } from "relay-hooks";
import OnboardingButton from "./OnboardingButton";
import useOnboardingData from "hooks/useOnboardingData";
import { useEffectOnce } from "react-use";
import { useState } from "react";
import { useLogout } from "../../../queries/users/logout";
import ErrorIcon from "@mui/icons-material/Error";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;
const Inner = styled.div`
  padding: 10px 25px;
  background: white;
  border-radius: 50px;
  width: calc(100% - 80px);
`;

const ErrorInner = styled.div`
  padding: 20px 25px;
  background: ${({ theme }) => theme.duo.color.lightRed};
  border-radius: 10px;
  color: ${({ theme }) => theme.duo.color.red};
  text-align: center;
  position: relative;
  border: 1px solid #ffcdd2;
`;

const ErrorIconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 10px;

  svg {
    font-size: 40px;
    color: ${({ theme }) => theme.duo.color.red};
  }
`;

const OnboardingSecondaryButton = styled(Button)`
  width: calc(100% - 60px);
  height: 60px;
  margin-bottom: 16px;
`;

const isEmailValid = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const SetEmail = ({ onRequestSubmit }) => {
  const setLoaderGlobal = useSetState("loaderGlobal");
  const logout = useLogout();
  const [isEmailVerificationFailed, setIsEmailVerificationFailed] = useState(false);
  const { saveOnboardingData } = useOnboardingData();
  const [editUser] = useMutation<UserEditMutation>(UserEditQL);
  const [email, setEmail] = useState("");
  const [disabled, setDisabled] = useState(true);

  const handleContinue = () => {
    setLoaderGlobal(true);
    editUser({
      variables: { email },
      onCompleted: () => {
        setLoaderGlobal(false);
        onRequestSubmit();
      },
      onError: () => {
        setIsEmailVerificationFailed(true);
        setLoaderGlobal(false); // on error user see will see "email is used..., try to login" and will stay on this step
      }
    });
  };

  const handleSignIn = () => {
    logout("/?switch_account=true");
  }

  const handleAddAnotherMail = () => {
    setEmail("");
    setIsEmailVerificationFailed(false);
  }

  // webfunnel users need to see notifications step when continuing in onboarding in App
  useEffectOnce(() => {
    saveOnboardingData({ notificationsPending: true });
  });

  return (
    <>
      {isEmailVerificationFailed ? (
        <>
          <Wrap>
            <ErrorInner>
              <ErrorIconContainer>
                <ErrorIcon />
              </ErrorIconContainer>
              <Trans>
                We already got your account:
              </Trans>
              <br />
              <b>{email}</b>
            </ErrorInner>
          </Wrap>
          <OnboardingSecondaryButton
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleAddAnotherMail}
          >
            <Trans>Add another email</Trans>
          </OnboardingSecondaryButton>
          <OnboardingButton onClick={handleSignIn} disabled={disabled}>
            <Trans>Login as an existing user</Trans>
          </OnboardingButton>
        </>
      ) : (
        <>
          <Wrap>
            <Inner>
              <TextField
                label={<Trans>Email</Trans>}
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setDisabled(!isEmailValid(e.target.value));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !disabled) {
                    handleContinue();
                  }
                }}
                variant="standard"
                autoComplete="email"
                autoFocus
                fullWidth
              />
            </Inner>
          </Wrap>
          <OnboardingButton onClick={handleContinue} disabled={disabled}>
            <Trans>Continue</Trans>
          </OnboardingButton>
        </>
      )}
    </>
  );
};

export default SetEmail;
